import React, { useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import '../../../collectibles/collectibles.scss';
import { DateTime } from 'luxon';
import {
  Invoice,
  InvoiceSearchSummaryData,
  SearchInvoiceRequest,
} from '../../../../clients/quipli-service/invoice/invoice';
import { useAPI } from '../../../../clients/quipli-service/quipli-service-client';
import { useInvoiceApiV1 } from '../../../../clients/quipli-service/invoice/invoice-api-v1';
import { Pagination } from '../../../../clients/quipli-service/pagination/pagination';
import { CollectionsFilters } from '../../../collectibles/components/collectibles-filters';
import { Card } from '../../../../components/card/card';
import { displayMoneyFormat } from '../../../../util/util';
import { InvoiceList } from '../../../collectibles/invoice-list';
import { Paginator } from '../../../../components/paginator/paginator-v2';
import { Location } from '../../../../clients/quipli-service/location/location';
import FileSaver from 'file-saver';
import { Button } from '../../../../components/form/button';
import Icon from '../../../../components/icons/icon';
import { User } from '../../../../clients/quipli-service/user/user';
import { PaymentModal } from '../../../payment/payment-modal';
import { TenantContext } from '../../../../context/tenant-context';
import { PaymentHistory } from '../../../collectibles/components/payment-history';
import { toast } from '../../../../util/toasts';
import { Payment } from '../../../../clients/quipli-service/payment/payment';
import { Dropdown } from '../../../../components/dropdown/dropdown';
import { Typography } from '../../../../components/typography/typography';
import { SpinnerModal } from '../../../../components/modal/spinner-modal';
import { Constants } from '../../../../constants/constants';
import { useAdmin } from '../../../../hooks/use-admin-context';

function extractInvoiceSearchParams(params: URLSearchParams): SearchInvoiceRequest {
  return {
    query: params.get('query') || undefined,
    date: {
      from: params.get('dateFrom') ? DateTime.fromISO(params.get('dateFrom')!) : undefined,
      to: params.get('dateTo') ? DateTime.fromISO(params.get('dateTo')!) : undefined,
    },
    dueDate: {
      from: params.get('dueDateFrom') ? DateTime.fromISO(params.get('dueDateFrom')!) : undefined,
      to: params.get('dueDateTo') ? DateTime.fromISO(params.get('dueDateTo')!) : undefined,
    },
    daysPastDue: params.get('daysPastDue') ? parseInt(params.get('daysPastDue')!) : undefined,
    locationId: params.get('locationId') ? parseInt(params.get('locationId')!) : undefined,
    addressId: params.get('addressId') ? parseInt(params.get('addressId')!) : undefined,
    invoiceStatusFilter: params.get('invoiceStatusFilter')?.split(',') || undefined,
    orderStatusFilter: params.get('orderStatusFilter'),
    orderReturnStatusFilter: params.get('orderReturnStatusFilter'),
    sortBy: params.get('sortBy') || 'dueDate',
    sortDirection: params.get('sortDirection') || 'desc',
    pageSize: params.get('pageSize') ? parseInt(params.get('pageSize')!) : undefined,
    pageNumber: params.get('pageNumber') ? parseInt(params.get('pageNumber')!) : undefined,
  } as SearchInvoiceRequest;
}

function injectInvoiceSearchParams(params: URLSearchParams, req: SearchInvoiceRequest): URLSearchParams {
  req.query ? params.set('query', req.query) : params.delete('query');
  req.date?.from ? params.set('dateFrom', req.date.from.toISO()) : params.delete('dateFrom');
  req.date?.to ? params.set('dateTo', req.date.to.toISO()) : params.delete('dateTo');
  req.dueDate?.from ? params.set('dueDateFrom', req.dueDate.from.toISO()) : params.delete('dueDateFrom');
  req.dueDate?.to ? params.set('dueDateTo', req.dueDate.to.toISO()) : params.delete('dueDateTo');
  req.daysPastDue ? params.set('daysPastDue', req.daysPastDue.toString()) : params.delete('daysPastDue');
  req.locationId ? params.set('locationId', req.locationId.toString()) : params.delete('locationId');
  req.addressId ? params.set('addressId', req.addressId.toString()) : params.delete('addressId');
  req.invoiceStatusFilter
    ? params.set('invoiceStatusFilter', req.invoiceStatusFilter.join(','))
    : params.delete('invoiceStatusFilter');
  req.orderStatusFilter ? params.set('orderStatusFilter', req.orderStatusFilter) : params.delete('orderStatusFilter');
  req.orderReturnStatusFilter
    ? params.set('orderReturnStatusFilter', req.orderReturnStatusFilter)
    : params.delete('orderReturnStatusFilter');
  req.sortBy ? params.set('sortBy', req.sortBy) : params.delete('sortBy');
  req.sortDirection ? params.set('sortDirection', req.sortDirection) : params.delete('sortDirection');
  req.pageSize ? params.set('pageSize', req.pageSize.toString()) : params.delete('pageSize');
  req.pageNumber ? params.set('pageNumber', req.pageNumber.toString()) : params.delete('pageNumber');

  return params;
}

export const UserCollectibles = ({ user }: { user: User }) => {
  const { getMultipleConfigurations, listLocations, getConfiguration, generateStatement, sendStatement } = useAPI();
  const { searchInvoices, searchSummaryData, exportInvoices } = useInvoiceApiV1();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchRequest, setSearchRequest] = useState<SearchInvoiceRequest>(extractInvoiceSearchParams(searchParams));
  const [currency, setCurrency] = useState<string>();
  const [displayTaxByJurisdiction, setDisplayTaxByJurisdiction] = useState<boolean>();
  const [invoices, setInvoices] = useState<Pagination<Invoice>>();
  const [locations, setLocations] = useState<Location[]>();
  const [summaryData, setSummaryData] = useState<InvoiceSearchSummaryData>();
  const [addStatementPayment, setAddStatementPayment] = useState<boolean>(false);
  const [allowStatementPayment, setAllowStatementPayment] = useState<boolean>(false);
  const [applicablePayments, setApplicablePayments] = useState<Payment[]>([]);
  const [credits, setCredits] = useState<Payment[]>([]);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [taxInclusive, setTaxInclusive] = useState<boolean>(false);
  const [taxLabel, setTaxLabel] = useState<string>('Tax');
  const { getApplicablePaymentSummary } = useInvoiceApiV1();
  const tenant = useContext(TenantContext);
  const { hasPermissions } = useAdmin();

  async function refreshInvoices(sortBy?: 'date' | 'dueDate' | 'invoiceNumber') {
    if (sortBy) {
      setSearchRequest((req) => {
        let sortDirection: 'asc' | 'desc' = 'asc';
        if (req.sortBy === sortBy) {
          sortDirection = req.sortDirection === 'asc' ? 'desc' : 'asc';
        }
        return {
          ...req,
          sortBy: sortBy || req.sortBy,
          sortDirection,
        };
      });
    } else if (searchRequest.pageSize) {
      await searchInvoices({ ...searchRequest, customerId: user.id }).then((res) => setInvoices(res));
      await searchSummaryData({ ...searchRequest, customerId: user.id }).then((res) => setSummaryData(res));
    }

    try {
      getApplicablePaymentSummary({ userId: user.id }).then((payments) => {
        setApplicablePayments(payments.unapplied);
        setCredits(payments.credits);
      });
    } catch (error) {
      toast('Failed to load applicable payments', 'error');
    }
  }

  useEffect(() => {
    if (!currency || displayTaxByJurisdiction === undefined) {
      getMultipleConfigurations([
        'CONF_CURRENCY',
        'CONF_DISPLAY_JURISDICTION_TAXES_BREAKDOWN',
        'CONF_SHOW_PRICES_WITH_TAX',
        'CONF_PRICE_TAX_LABEL',
      ]).then((confs) => {
        setCurrency(confs.find((c) => c.variable === 'CONF_CURRENCY')?.value || 'USD');
        setDisplayTaxByJurisdiction(
          confs.find((c) => c.variable === 'CONF_DISPLAY_JURISDICTION_TAXES_BREAKDOWN')?.value === '1',
        );
        setTaxInclusive(confs.find((config) => config.variable == 'CONF_SHOW_PRICES_WITH_TAX')?.value === '1');
        setTaxLabel(confs.find((config) => config.variable == 'CONF_PRICE_TAX_LABEL')?.value || 'Tax');
      });
    }
    if (!locations) {
      listLocations().then(setLocations);
    }
    getConfiguration('CONF_STATEMENT_PAYMENTS').then((conf) => {
      setAllowStatementPayment(conf?.value === '1');
    });

    setSearchParams(injectInvoiceSearchParams(searchParams, searchRequest));
    refreshInvoices();
  }, [searchRequest]);

  const doExport = async () => {
    setIsExporting(true);
    try {
      const { pageSize, pageNumber, ...req } = searchRequest;
      const file = await exportInvoices({ ...req, customerId: user.id });

      const exportFileName = `Collectibles_${user.name}_${DateTime.local({
        zone: locations?.find(() => true)?.timezone,
      }).toISODate()}`;
      const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
      const fileExtension = '.xlsx';

      const data = new Blob([file], { type: fileType });
      FileSaver.saveAs(data, `${exportFileName}${fileExtension}`);
    } catch (error) {
      toast('Failed to export invoices', 'error');
      console.error(error);
    } finally {
      setIsExporting(false);
    }
  };

  const doGenerateStatement = async () => {
    try {
      const file = await generateStatement(user.id, { addressId: searchRequest.addressId });
      const fileType = 'application/pdf';

      const data = new Blob([file], { type: fileType });

      window.open(URL.createObjectURL(data));
    } catch (error: any) {
      toast(error.message || 'Failed to generate statement', 'error');
    }
  };

  return (
    <>
      <div className="collectibles flex flex-column gap-4">
        <div className="flex justify-between">
          <CollectionsFilters
            locations={locations || []}
            searchRequest={{
              ...searchRequest,
              customerId: user.id,
            }}
            setSearchRequest={setSearchRequest}
            disabledFilters={['customer']}
            activeFilters={0}
          />
          <div className={'flex gap-4'}>
            {allowStatementPayment && !user.paymentBan && (
              <Button
                disabled={!hasPermissions(Constants.Permissions.Refund_Upcharge_Order)}
                className="button-yellow h-[46px]"
                onClick={() => setAddStatementPayment(true)}
                data-cy="add-statement-payment"
              >
                Make a Payment
                <Icon icon="Money" size="medium" stroke="var(--grey-700)" />
              </Button>
            )}
            <div>
              <Dropdown
                trigger={() => (
                  <Button className="button-yellow h-[46px]">
                    <Typography type="text-sm">Statement</Typography>
                    <Icon icon="ChevronDown" size="small" stroke="var(--grey-700)" />
                  </Button>
                )}
              >
                <div className="flex items-center gap-2" onClick={() => doGenerateStatement()}>
                  <Icon icon="Download" size="medium" stroke="var(--grey-700)" />
                  <Typography type="text-sm">Download</Typography>
                </div>
                <div
                  className="flex items-center gap-2"
                  onClick={() =>
                    sendStatement(user.id)
                      .then(() => toast('Statement email sent'))
                      .catch(() => toast('Failed to send statement email', 'error'))
                  }
                >
                  <Icon icon="Envelope" size="medium" stroke="var(--grey-700)" />
                  <Typography type="text-sm">Send</Typography>
                </div>
              </Dropdown>
            </div>
            <Button className="button-yellow h-[46px]" onClick={() => doExport()} data-cy="export">
              Export
              <Icon icon="Export" size="medium" stroke="var(--grey-700)" />
            </Button>
          </div>
        </div>
        <div className="summary-row">
          <Card className="flex flex-column grow">
            <span>Past Due</span>
            <span>{displayMoneyFormat(summaryData?.pastDue, currency)}</span>
          </Card>
          <Card className="flex flex-column grow">
            <span>Payable</span>
            <span>{displayMoneyFormat(summaryData?.payable, currency)}</span>
          </Card>
          <Card className="flex flex-column grow">
            <span>Received</span>
            <span>{displayMoneyFormat(summaryData?.received, currency)}</span>
          </Card>
        </div>

        {applicablePayments?.length > 0 && (
          <Card>
            <PaymentHistory
              payments={applicablePayments}
              title={'Unapplied payments'}
              refresh={refreshInvoices}
            ></PaymentHistory>
          </Card>
        )}

        {credits?.length > 0 && (
          <Card>
            <PaymentHistory payments={credits} title={'Credits'} refresh={refreshInvoices}></PaymentHistory>
          </Card>
        )}

        <Card>
          {invoices && currency && displayTaxByJurisdiction !== undefined && (
            <InvoiceList
              invoices={invoices.items}
              locations={locations}
              currency={currency}
              refresh={refreshInvoices}
              sortable={true}
              sort={
                searchRequest.sortBy &&
                searchRequest.sortDirection && {
                  field: searchRequest.sortBy,
                  direction: searchRequest.sortDirection,
                }
              }
              enabledColumns={[
                'CheckBoxColumn',
                'InvoiceNumber',
                'Location',
                'DueDate',
                'Date',
                'Status',
                'DaysPastDue',
                'Tax',
                'Total',
                'Payments',
                'Balance',
                'Actions',
              ]}
              displayTaxByJurisdiction={displayTaxByJurisdiction}
              taxInclusive={taxInclusive}
              taxLabel={taxLabel}
            />
          )}
          <Paginator
            pagination={invoices?.pagination}
            request={searchRequest}
            setRequest={(pag) =>
              setSearchRequest((req) => {
                return { ...req, ...pag };
              })
            }
          />
        </Card>
      </div>
      {addStatementPayment && locations && (
        <PaymentModal
          isOpen={!!addStatementPayment}
          close={async (payments) => {
            setAddStatementPayment(false);
            if (payments) {
              await refreshInvoices();
            }
          }}
          currency={tenant.configuration!.currency}
          user={user}
          balance={summaryData?.payable && summaryData.payable > 0 ? summaryData?.payable : 0}
          statementPayment={true}
          locations={locations}
        />
      )}
      <SpinnerModal isOpen={isExporting} contentDescription={'Exporting collectibles'} />
    </>
  );
};
